import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { translations } from '../../utils/translations';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Checkbox } from '../../components/ui/checkbox';
import { Loader2, Eye, EyeOff } from 'lucide-react';

// Brand logo - Nueva imagen C&M 2024
const LOGO_LIGHT = 'https://customer-assets.emergentagent.com/job_5612798b-7eab-4f57-bce1-b7ba348a317b/artifacts/qgvhhx6f_fav%202.jpeg';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { language, t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Obtener el redirect desde la URL
  const redirectTo = searchParams.get('redirect');

  // Cargar credenciales guardadas
  useEffect(() => {
    const savedEmail = localStorage.getItem('cym_remembered_email');
    const savedRemember = localStorage.getItem('cym_remember_me');
    if (savedRemember === 'true' && savedEmail) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Guardar o eliminar email según "Recordarme"
    if (rememberMe) {
      localStorage.setItem('cym_remembered_email', email);
      localStorage.setItem('cym_remember_me', 'true');
    } else {
      localStorage.removeItem('cym_remembered_email');
      localStorage.removeItem('cym_remember_me');
    }

    try {
      const { user, mustChangePassword } = await login(email, password);
      
      if (mustChangePassword) {
        navigate('/cambiar-password');
      } else if (user.role === 'super_admin' || user.role === 'admin' || user.role === 'admin_finanzas') {
        navigate('/admin');
      } else {
        // Si hay un redirect específico y es usuario de taller, usarlo
        if (redirectTo && (user.role === 'taller_owner' || user.role === 'taller_user')) {
          navigate(redirectTo);
        } else {
          navigate('/panel');
        }
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(err.response?.data?.detail || t(translations.login.errorCredenciales));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-teal-900 to-slate-900 flex items-center justify-center p-4">
      {/* Background pattern */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4wMyI+PHBhdGggZD0iTTM2IDM0djItSDI0di0yaDEyem0wLTRWMjhIMjR2MmgxMnptLTgtOHYtMmgydjJoLTJ6bTggMHYtMmgydjJoLTJ6bS04IDR2LTJoMnYyaC0yem04IDB2LTJoMnYyaC0yeiIvPjwvZz48L2c+PC9zdmc+')] opacity-50" />
      
      <Card className="w-full max-w-md relative shadow-2xl border-0">
        <CardHeader className="text-center pb-2">
          <div className="flex justify-center mb-6">
            <img 
              src={LOGO_LIGHT} 
              alt="C&M Servicios Aeronáuticos" 
              className="h-24 w-auto drop-shadow-lg"
            />
          </div>
          <CardTitle className="text-2xl text-slate-800">{t(translations.login.titulo)}</CardTitle>
          <CardDescription>{t(translations.login.subtitulo)}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <Alert variant="destructive" className="bg-red-50 border-red-200">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-700">{t(translations.login.email)}</Label>
              <Input
                id="email"
                type="email"
                placeholder={language === 'pt' ? 'seu@email.com' : 'tu@email.com'}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="border-slate-200 focus:border-teal-500 focus:ring-teal-500"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-700">{t(translations.login.password)}</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="border-slate-200 focus:border-teal-500 focus:ring-teal-500 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="remember" 
                  checked={rememberMe}
                  onCheckedChange={(checked) => setRememberMe(checked)}
                />
                <Label htmlFor="remember" className="text-sm text-slate-600 cursor-pointer">
                  {t(translations.login.recordarEmail)}
                </Label>
              </div>
              <Link to="/forgot-password" className="text-sm text-teal-600 hover:text-teal-700 hover:underline">
                ¿Olvidaste tu contraseña?
              </Link>
            </div>
            
            <Button 
              type="submit" 
              className="w-full bg-teal-500 hover:bg-teal-600 text-white shadow-lg shadow-teal-500/25" 
              disabled={loading}
            >
              {loading ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t(translations.login.ingresando)}</>
              ) : (
                t(translations.login.ingresar)
              )}
            </Button>
          </form>
          
          <div className="mt-6 text-center text-sm">
            <p className="text-gray-600">
              {t(translations.login.noTienesCuenta)}{' '}
              <Link to="/suscripcion" className="text-teal-600 hover:text-teal-700 font-medium hover:underline">
                {t(translations.login.solicitaSuscripcion)}
              </Link>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default LoginPage;
